import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/hooks/useCartContext';
import LanguageSwitcher from '@/components/base/LanguageSwitcher';
import FutureIcon, { type FutureIconName } from '@/components/base/FutureIcon';

function SereiaLogo({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const dims = size === 'sm' ? { w: 28, h: 24 } : size === 'md' ? { w: 36, h: 30 } : { w: 44, h: 36 };

  return (
    <svg
      width={dims.w}
      height={dims.h}
      viewBox="0 0 100 90"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="sereia-container sereia-glow flex-shrink-0"
      aria-label="Sereia AQUAMARINA"
    >
      <defs>
        <linearGradient id={`sereiaGrad-${size}`} x1="50%" y1="60%" x2="50%" y2="100%">
          <stop offset="0%" stopColor="#00ACC1" />
          <stop offset="100%" stopColor="#4DD0E1" />
        </linearGradient>
        <linearGradient id={`sereiaHair-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00ACC1" />
          <stop offset="50%" stopColor="#4DD0E1" />
          <stop offset="100%" stopColor="#00ACC1" />
        </linearGradient>
      </defs>

      {/* Cauda de sereia com escamas */}
      <g className="sereia-tail" style={{ transformOrigin: '50px 58px' }}>
        <path
          d="M48 58 C45 68 38 78 28 85 C22 89 18 86 22 82 C30 76 38 68 42 58"
          fill={`url(#sereiaGrad-${size})`}
          opacity="0.85"
        />
        <path
          d="M52 58 C55 68 62 78 72 85 C78 89 82 86 78 82 C70 76 62 68 58 58"
          fill={`url(#sereiaGrad-${size})`}
          opacity="0.85"
        />
        <path
          d="M28 85 C20 92 12 88 18 82"
          fill="#00ACC1"
          opacity="0.6"
        />
        <path
          d="M72 85 C80 92 88 88 82 82"
          fill="#00ACC1"
          opacity="0.6"
        />
        <ellipse cx="44" cy="65" rx="2.5" ry="1.5" fill="#00ACC1" opacity="0.5" />
        <ellipse cx="40" cy="70" rx="2" ry="1.2" fill="#4DD0E1" opacity="0.4" />
        <ellipse cx="36" cy="75" rx="1.8" ry="1" fill="#00ACC1" opacity="0.45" />
        <ellipse cx="56" cy="65" rx="2.5" ry="1.5" fill="#00ACC1" opacity="0.5" />
        <ellipse cx="60" cy="70" rx="2" ry="1.2" fill="#4DD0E1" opacity="0.4" />
        <ellipse cx="64" cy="75" rx="1.8" ry="1" fill="#00ACC1" opacity="0.45" />
        <ellipse cx="48" cy="62" rx="2" ry="1.2" fill="#4DD0E1" opacity="0.35" />
        <ellipse cx="52" cy="62" rx="2" ry="1.2" fill="#4DD0E1" opacity="0.35" />
      </g>

      <path
        d="M46 32 C46 40 44 50 48 58 C50 60 52 60 52 58 C56 50 54 40 54 32"
        fill="rgba(10, 52, 66, 0.12)"
        stroke="#00ACC1"
        strokeWidth="0.8"
        opacity="0.9"
      />

      <path
        d="M46 36 C38 42 32 46 28 44"
        stroke="#00ACC1"
        strokeWidth="0.8"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
      />
      <path
        d="M54 36 C62 42 68 46 72 44"
        stroke="#00ACC1"
        strokeWidth="0.8"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
      />

      <g className="sereia-hair" style={{ transformOrigin: '50px 18px' }}>
        <path
          d="M45 18 C30 14 18 18 12 28"
          stroke={`url(#sereiaHair-${size})`}
          strokeWidth="1.2"
          strokeLinecap="round"
          fill="none"
          opacity="0.8"
        />
        <path
          d="M42 16 C28 10 16 14 10 22"
          stroke="#4DD0E1"
          strokeWidth="0.8"
          strokeLinecap="round"
          fill="none"
          opacity="0.6"
        />
        <path
          d="M55 18 C70 14 82 18 88 28"
          stroke={`url(#sereiaHair-${size})`}
          strokeWidth="1.2"
          strokeLinecap="round"
          fill="none"
          opacity="0.8"
        />
        <path
          d="M58 16 C72 10 84 14 90 22"
          stroke="#4DD0E1"
          strokeWidth="0.8"
          strokeLinecap="round"
          fill="none"
          opacity="0.6"
        />
        <path
          d="M48 14 C42 8 36 12 32 20"
          stroke="#00ACC1"
          strokeWidth="0.6"
          strokeLinecap="round"
          fill="none"
          opacity="0.5"
        />
        <path
          d="M52 14 C58 8 64 12 68 20"
          stroke="#00ACC1"
          strokeWidth="0.6"
          strokeLinecap="round"
          fill="none"
          opacity="0.5"
        />
      </g>

      <ellipse
        cx="50"
        cy="18"
        rx="7"
        ry="8"
        fill="rgba(255, 255, 255, 0.95)"
        stroke="#00ACC1"
        strokeWidth="0.6"
      />
      <circle cx="47" cy="17" r="1" fill="#0A3442" />
      <circle cx="53" cy="17" r="1" fill="#0A3442" />
      <path
        d="M47 21 Q50 23 53 21"
        stroke="#00ACC1"
        strokeWidth="0.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.7"
      />
      <circle cx="50" cy="8" r="2" fill="#4DD0E1" opacity="0.6" />
      <path
        d="M48 8 L46 4 L50 6 L54 4 L52 8"
        fill="#F5A623"
        opacity="0.5"
      />
    </svg>
  );
}

/** Tour categories: always visible as a rail under the main bar, on every page and screen size. */
const CATEGORY_LINKS: { to: string; railKey: string; icon: FutureIconName }[] = [
  { to: '/escunas', railKey: 'escunas', icon: 'schooner' },
  { to: '/lanchas-privadas', railKey: 'privadas', icon: 'speedboat' },
  { to: '/lanchas-compartilhadas', railKey: 'compartilhadas', icon: 'shared' },
  { to: '/passeios-de-jeep', railKey: 'jeep', icon: 'jeep' },
  { to: '/expedicao-bolivia', railKey: 'bolivia', icon: 'mountains' },
];

const PAGE_LINKS: { to: string; labelKey: string }[] = [
  { to: '/sobre-nos', labelKey: 'nav.about' },
  { to: '/contato', labelKey: 'nav.contact' },
];

// Pages whose top is the dark ocean header get the light-on-dark bar until the visitor scrolls
const DARK_HEADER_ROUTES = ['/', ...CATEGORY_LINKS.map((link) => link.to)];

/** Two-line label: small context line on top, the category word below. */
function RailLabel({ railKey }: { railKey: string }) {
  const { t } = useTranslation();
  return (
    <span className="rail-label">
      <span className="rail-top">{t(`rail.categories.${railKey}.top`)}</span>
      <span className="rail-bottom">{t(`rail.categories.${railKey}.bottom`)}</span>
    </span>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const lastScrollY = useRef(0);
  const railRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const { totalItems } = useCart();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // A media query, not window.innerWidth: reading innerWidth in a scroll handler forces a synchronous
    // style + layout pass of the whole page on every scroll event.
    const desktop = window.matchMedia('(min-width: 768px)');
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (desktop.matches || currentY < 120) {
        setNavVisible(true);
      } else {
        setNavVisible(currentY < lastScrollY.current);
      }
      lastScrollY.current = currentY;
      setScrolled(currentY > 40);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    desktop.addEventListener('change', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      desktop.removeEventListener('change', handleScroll);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setNavVisible(true);
  }, [pathname]);

  // The highlight pill slides to the active category (re-measured on route, language and size changes)
  useLayoutEffect(() => {
    const rail = railRef.current;
    const indicator = indicatorRef.current;
    if (!rail || !indicator) return;
    const place = () => {
      const active = rail.querySelector<HTMLElement>('.rail-chip.is-active');
      if (!active) {
        indicator.style.opacity = '0';
        return;
      }
      indicator.style.opacity = '1';
      indicator.style.width = `${active.offsetWidth}px`;
      indicator.style.height = `${active.offsetHeight}px`;
      indicator.style.transform = `translate3d(${active.offsetLeft}px, ${active.offsetTop}px, 0)`;
    };
    place();
    const observer = new ResizeObserver(place);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [pathname, i18n.language]);

  // "Reservar" jumps to the contact block on the home page; elsewhere it opens the contact page
  const goToContact = useCallback(() => {
    setMobileOpen(false);
    const el = document.querySelector('#contato');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/contato');
    }
  }, [navigate]);

  const goToAllTours = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setMobileOpen(false);
    if (pathname === '/') {
      document.querySelector('#passeios')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#passeios');
    }
  }, [navigate, pathname]);

  const hasDarkHeader = DARK_HEADER_ROUTES.includes(pathname) || pathname.startsWith('/passeio/');
  const onDark = hasDarkHeader && !scrolled && !mobileOpen;

  return (
    <header
      className={`site-nav fixed top-0 left-0 right-0 z-50 ${scrolled || mobileOpen ? 'glass-nav' : 'bg-transparent'} ${
        onDark ? 'nav-on-dark' : ''
      } ${navVisible ? 'translate-y-0' : '-translate-y-[120%]'}`}
    >
      <nav className="w-full px-3 md:px-6 lg:px-8" aria-label={t('nav.menu')}>
        <div className="flex items-center justify-between gap-4 h-16 md:h-[72px]">
          <Link to="/" className="flex-shrink-0 flex items-center gap-2 md:gap-3 group sereia-container" aria-label="AQUAMARINA TOURS">
            <span className="hidden md:inline-block">
              <SereiaLogo size="md" />
            </span>
            <span className="md:hidden inline-block">
              <SereiaLogo size="sm" />
            </span>
            <span className={`brand-word text-base md:text-lg whitespace-nowrap transition-colors duration-300 ${onDark ? 'text-white' : 'text-pearl group-hover:text-foam'}`}>
              AQUAMARINA
            </span>
          </Link>

          <div className="hidden lg:flex items-center flex-1 justify-end gap-0.5 pr-2">
            {PAGE_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} className={({ isActive }) => `nav-link ${isActive ? 'is-active' : ''}`}>
                {t(link.labelKey)}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
            <LanguageSwitcher />

            <Link
              to="/carrinho"
              className="nav-icon-btn relative grid place-items-center min-h-11 min-w-11 rounded-full"
              aria-label={t('nav.cart')}
            >
              <FutureIcon name="bag" />
              {totalItems > 0 && (
                <span key={totalItems} className="cart-count cart-count--sm">{totalItems}</span>
              )}
            </Link>

            <span className="hidden md:block">
              <button type="button" onClick={goToContact} className="nav-cta">
                <FutureIcon name="calendar" />
                {t('nav.reserve')}
              </button>
            </span>

            <button
              type="button"
              className="nav-icon-btn lg:hidden grid place-items-center min-h-11 min-w-11 rounded-full"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t('nav.menu')}
              aria-expanded={mobileOpen}
            >
              <FutureIcon name={mobileOpen ? 'close' : 'menu'} key={mobileOpen ? 'close' : 'menu'} />
            </button>
          </div>
        </div>
      </nav>

      {/* Category rail: every category visible at once (one row on desktop, 3×2 grid on phones) */}
      <div className="category-rail-wrap">
        <div ref={railRef} className="category-rail" role="navigation" aria-label={t('nav.categories')}>
          <span ref={indicatorRef} className="rail-indicator" aria-hidden="true" />
          <a href="/#passeios" onClick={goToAllTours} className={`rail-chip ${pathname === '/' ? 'is-active' : ''}`} aria-current={pathname === '/' ? 'page' : undefined}>
            <span className="rail-icon"><FutureIcon name="compass" /></span>
            <RailLabel railKey="all" />
          </a>
          {CATEGORY_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} className={({ isActive }) => `rail-chip ${isActive ? 'is-active' : ''}`}>
              <span className="rail-icon"><FutureIcon name={link.icon} /></span>
              <RailLabel railKey={link.railKey} />
            </NavLink>
          ))}
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden mobile-menu border-t border-reef/15">
          <div className="px-4 pt-2 pb-4 grid gap-1 max-w-3xl mx-auto">
            {PAGE_LINKS.map((link, i) => (
              <NavLink
                key={link.to}
                to={link.to}
                style={{ animationDelay: `${60 + i * 45}ms` }}
                className={({ isActive }) =>
                  `flex items-center justify-between px-3 py-3 rounded-xl font-display text-[15px] font-medium border-b border-reef/10 ${isActive ? 'text-foam' : 'text-pearl'}`
                }
              >
                {t(link.labelKey)}
                <i className="ri-arrow-right-up-line text-reef" aria-hidden="true" />
              </NavLink>
            ))}
            <button type="button" onClick={goToContact} className="nav-cta justify-center mt-3 py-3 text-sm">
              <FutureIcon name="calendar" />
              {t('nav.reserve')}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
